import type { ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { SelectContext } from "./SelectContext";
import type { SelectCreator } from "./SelectCreateRow";
import { useSelect } from "./useSelect";
import { SelectTrigger } from "./SelectTrigger";
import { SelectValue } from "./SelectValue";
import { SelectContent } from "./SelectContent";
import { SelectItem } from "./SelectItem";
import { SelectGroup } from "./SelectGroup";
import { SelectLabel } from "./SelectLabel";
import { SelectSeparator } from "./SelectSeparator";

interface SelectBaseProps<TValue extends string> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  /** Passed to the underlying Popover. Use inside dialogs whose scroll lock would otherwise block the list. */
  modal?: boolean;
  /**
   * Labels a selected value whose item hasn't been rendered — e.g. a saved selection before
   * server-side search results include it. Used by the trigger, the multi-select badges, and the
   * selected rows pinned to the top of the list.
   */
  fallbackLabel?: (value: TValue) => ReactNode;
  /**
   * Offers the search text as a new option, in a row at the top of the list. It shows only while the
   * trimmed text is non-empty, matches no item's value or text (ignoring case), and passes `isValid`.
   * Choosing it selects `transform(text)` (toggling it in for a multi-select) and labels the new value
   * with the typed text. Setting it always shows the search input; `true` uses the defaults.
   */
  creator?: true | SelectCreator<TValue>;
  children?: ReactNode;
}

interface SingleSelectBaseProps<TValue extends string> extends SelectBaseProps<TValue> {
  multiple?: false;
  /**
   * The selected value. Passing the prop at all makes the Select controlled, even as `undefined`,
   * which means nothing is selected (e.g. an "add one" picker that never shows a selection: `value={undefined}`).
   * Omit it to leave the Select uncontrolled.
   */
  value?: TValue;
  defaultValue?: TValue;
}

export interface UnclearableSingleSelectProps<TValue extends string = string> extends SingleSelectBaseProps<TValue> {
  clearable?: false;
  onValueChange?: (value: TValue) => void;
}

export interface ClearableSingleSelectProps<TValue extends string = string> extends SingleSelectBaseProps<TValue> {
  /**
   * Renders a clear button beside `Select.Trigger` while something is selected; clearing reports
   * `undefined` through `onValueChange`. `className` on the trigger then sizes the field and the
   * button together. Not rendered for an `asChild` trigger.
   */
  clearable: boolean;
  onValueChange?: (value: TValue | undefined) => void;
}

/**
 * Single-select props: one value at a time; choosing an item closes the dropdown. A `clearable`
 * single-select's `onValueChange` must also accept `undefined`, which it receives when cleared.
 */
export type SingleSelectProps<TValue extends string = string> =
  | UnclearableSingleSelectProps<TValue>
  | ClearableSingleSelectProps<TValue>;

/** Multi-select props: choosing an item toggles it, the dropdown stays open, and the selection shows as badges under the trigger. */
export interface MultiSelectProps<TValue extends string = string> extends SelectBaseProps<TValue> {
  multiple: true;
  /**
   * Renders a clear button beside `Select.Trigger` while something is selected; clearing reports `[]`
   * through `onValueChange`. `className` on the trigger then sizes the field and the button together.
   * Not rendered for an `asChild` trigger.
   */
  clearable?: boolean;
  /** The selected values. Passing the prop at all makes the Select controlled (`undefined` meaning none); omit it to leave the Select uncontrolled. */
  value?: TValue[];
  defaultValue?: TValue[];
  onValueChange?: (value: TValue[]) => void;
  /** Once this many values are selected, the remaining unselected items are disabled. */
  maxCount?: number;
  /** Badges beyond this many collapse into a single "+N more" badge. */
  maxDisplayCount?: number;
}

/**
 * Props for {@link Select}, optionally narrowed to a string-literal union. Pass `multiple` for a
 * multi-select, whose `value`/`onValueChange` work with arrays.
 */
export type SelectProps<TValue extends string = string> = SingleSelectProps<TValue> | MultiSelectProps<TValue>;

/**
 * A dropdown composed from `Select.Trigger` / `Select.Value` / `Select.Content` / `Select.Item`,
 * with optional `Select.Group` / `Select.Label` / `Select.Separator`. `clearable` adds a clear
 * button beside the trigger; `creator` adds a row for committing typed text.
 *
 * **Searching.** `Select.Content` shows a search input inside the dropdown once it holds more than
 * `searchThreshold` items (default 5); `searchable` forces it on or off. By default the Select filters
 * the rendered items itself. To filter them yourself (e.g. through a search query), pass
 * `filter="external"`: the typed text arrives through `onSearchChange` and the items you render are
 * shown as-is:
 *
 * ```tsx
 * <Select value={formId} onValueChange={setFormId}>
 *   <Select.Trigger><Select.Value placeholder={t("Select a form")} /></Select.Trigger>
 *   <Select.Content filter="external" search={search} onSearchChange={setSearch} loading={isLoading}>
 *     {forms.map((form) => <Select.Item key={form.id} value={form.id}>{form.title}</Select.Item>)}
 *   </Select.Content>
 * </Select>
 * ```
 *
 * The trigger shows the selected item's children, even before the dropdown first opens and after a
 * search filters it out. When the selected item may never be rendered (external filtering), give it a label
 * with `fallbackLabel` (or `Select.Value`'s `fallback`).
 *
 * **Multi-select.** With `multiple`, each choice toggles a value and the dropdown stays open; the
 * selection renders as removable badges under the trigger:
 *
 * ```tsx
 * <Select multiple value={userIds} onValueChange={setUserIds} maxDisplayCount={5}>
 *   <Select.Trigger><Select.Value placeholder={t("Any user")} /></Select.Trigger>
 *   <Select.Content>
 *     {users.map((user) => <Select.Item key={user.id} value={user.id}>{formatName(user)}</Select.Item>)}
 *   </Select.Content>
 * </Select>
 * ```
 *
 * **Typed values.** Pass a type argument to preserve a string-literal union, so the value feeds back
 * into typed state without a cast (`<Select<Status> value={status} onValueChange={setStatus}>`). The
 * narrowing assumes every `Select.Item` carries one of `TValue`'s members; validate at the boundary
 * when the options come from outside the type.
 */
const SelectRoot = <TValue extends string = string>(props: SelectProps<TValue>) => {
  const { context, isOpen, setOpen, modal } = useSelect(props);

  return (
    <SelectContext.Provider value={context}>
      <PopoverPrimitive.Root open={isOpen} onOpenChange={setOpen} modal={modal}>
        {props.children}
      </PopoverPrimitive.Root>
    </SelectContext.Provider>
  );
};

export const Select = Object.assign(SelectRoot, {
  Trigger: SelectTrigger,
  Value: SelectValue,
  Content: SelectContent,
  Item: SelectItem,
  Group: SelectGroup,
  Label: SelectLabel,
  Separator: SelectSeparator,
});
